/**
 * ThemeSync — renders nothing; mirrors SkyProvider's mode onto the app. A copy
 * of skySchedule's components/shell/ThemeSync.tsx.
 *
 * SkyProvider holds the mode and toggles its own `sky-dark` class, but the
 * choice has to outlive a reload. Mounted once, inside the signed-in app's
 * SkyProvider, so every setMode() — from the profile menu or anywhere else —
 * lands here. See lib/theme.ts for the full picture.
 */
import { useEffect, useRef } from "react";
import { useSkyTheme } from "@skypointcloud/skypoint-ui";
import { applyColorScheme, writeThemePreference } from "../../lib/theme";

export function ThemeSync() {
  const { mode, isDarkMode } = useSkyTheme();

  // isDarkMode already resolves 'system' against prefers-color-scheme and
  // updates when the OS switches, so this follows a live OS change too.
  useEffect(() => {
    applyColorScheme(isDarkMode);
  }, [isDarkMode]);

  // Save only a choice the user actually made. Writing the starting mode would
  // pin everyone who never opened the switcher to today's default, so a later
  // default change would never reach them. Compared against the starting value
  // rather than "skip the first run" so a double-invoked effect can't save it.
  const startingMode = useRef(mode);
  const changed = useRef(false);
  useEffect(() => {
    if (!changed.current && mode === startingMode.current) return;
    changed.current = true;
    writeThemePreference(mode);
  }, [mode]);

  return null;
}
